import { FlaskConical } from 'lucide-react';

import { useDiscussoesControllerObterResultadosDoExame } from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { ICONE_INTERPRETACAO, ROTULO_INTERPRETACAO } from '@/features/exames/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

interface Props {
  /** `null` = fechado. Controla o `enabled` da busca — só consulta com o diálogo aberto. */
  exameId: string | null;
  aoFechar: () => void;
}

/**
 * Resultados de um exame INTERNO (do catálogo) compartilhado na discussão —
 * mesma tabela que `LaudoDoExamePage` mostra pro médico, num diálogo em vez
 * de tela cheia. Usa `GET /discussoes/exames-compartilhados/:exameId/resultados`,
 * escopada por participação na discussão (não por tenant).
 */
export function ResultadosDoExameDialogo({ exameId, aoFechar }: Props) {
  const resultado = useDiscussoesControllerObterResultadosDoExame(exameId ?? '', {
    query: { enabled: exameId !== null },
  });

  return (
    <Dialog open={exameId !== null} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{resultado.data?.exameNome ?? 'Resultados do exame'}</DialogTitle>
        </DialogHeader>

        {resultado.isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-40 rounded-md" />
          </div>
        ) : resultado.isError || !resultado.data ? (
          <EstadoErro aoTentarDeNovo={() => void resultado.refetch()} />
        ) : resultado.data.resultados.length === 0 ? (
          <EstadoVazio
            icone={FlaskConical}
            titulo="Aguardando resultado"
            descricao={`Nenhum dos ${resultado.data.totalAnalitos} analitos foi lançado até agora.`}
          />
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {resultado.data.resultados.length} de {resultado.data.totalAnalitos} exames
              lançados.
            </p>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Exames</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Unidade</TableHead>
                    <TableHead>Referência</TableHead>
                    <TableHead>Interpretação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resultado.data.resultados.map((r) => {
                    const chave = r.interpretacao ?? 'INDETERMINADO';
                    const { icone: Icone, classe } =
                      ICONE_INTERPRETACAO[chave] ?? ICONE_INTERPRETACAO.INDETERMINADO!;

                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium text-foreground">
                          {r.analitoNome}
                        </TableCell>
                        <TableCell className="text-right font-medium text-foreground">
                          {r.valorNumerico?.toLocaleString('pt-BR') ?? r.valorTexto ?? (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{r.unidade ?? '—'}</TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {r.faixaDeReferencia ?? (
                            <span className="italic">Não cadastrada para esta espécie</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-2">
                            <span
                              className={`w-6 h-6 rounded flex items-center justify-center ${classe}`}
                            >
                              <Icone className="w-3.5 h-3.5" />
                            </span>
                            <span className="text-sm text-foreground">
                              {ROTULO_INTERPRETACAO[chave] ?? chave}
                            </span>
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
